import type {
  ApiResponse,
  WidgetInitResponse,
  ConversationResponse,
  SendMessageResponse,
  Message,
  PaymentIntentResponse,
  PaymentStatusResponse,
  SpeechToTextResponse,
  StartConversationRequest,
} from '../types';

// Transform backend snake_case metadata to frontend camelCase
function transformMetadata(raw: Record<string, unknown> | null): Message['metadata'] {
  if (!raw || !raw.type) return raw as Message['metadata'];

  if (raw.type === 'offer') {
    return {
      type: 'offer',
      serviceId: raw.service_id as string,
      serviceName: raw.service_name as string,
      priceCents: raw.price_cents as number,
      currency: raw.currency as string,
      offerMessage: raw.offer_message as string,
      highlights: raw.highlights as string[] | undefined,
    };
  }

  if (raw.type === 'payment_form') {
    return {
      type: 'payment_form',
      clientSecret: raw.client_secret as string,
      amountCents: raw.amount_cents as number,
      currency: raw.currency as string,
      orderId: raw.order_id as string,
    };
  }

  return raw as Message['metadata'];
}

// Transform backend snake_case message to frontend camelCase Message
function transformMessage(msg: Record<string, unknown>, conversationId: string): Message {
  return {
    id: msg.id as string,
    conversationId,
    role: msg.role as 'user' | 'assistant',
    content: msg.content as string,
    metadata: transformMetadata(msg.metadata as Record<string, unknown> | null),
    wasVoiceInput: (msg.was_voice_input as boolean) || false,
    createdAt: (msg.created_at as string) || new Date().toISOString(),
  };
}

// Transform backend conversation response to frontend ConversationResponse
function transformConversation(data: Record<string, unknown>): ConversationResponse {
  const id = data.id as string;
  const messages = (data.messages as Record<string, unknown>[]) || [];
  return {
    conversationId: id,
    sessionId: (data.session_id as string) || '',
    status: data.status as ConversationResponse['status'],
    messages: messages.map((m) => transformMessage(m, id)),
    createdAt: (data.started_at as string) || (data.created_at as string) || new Date().toISOString(),
    updatedAt: (data.last_activity_at as string) || (data.updated_at as string) || new Date().toISOString(),
  };
}

// Transform backend send message response to frontend SendMessageResponse
function transformSendMessageResponse(data: Record<string, unknown>, conversationId: string): SendMessageResponse {
  const userMsg = data.user_message as Record<string, unknown>;
  const assistantMsg = data.assistant_message as Record<string, unknown>;
  return {
    message: transformMessage(userMsg, conversationId),
    assistantMessage: transformMessage(assistantMsg, conversationId),
  };
}

export class ApiError extends Error {
  code: string;
  details?: Record<string, unknown>;

  constructor(code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.details = details;
  }
}

export class ApiService {
  private baseUrl: string;
  private tenantSlug: string;

  constructor(baseUrl: string, tenantSlug: string) {
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.tenantSlug = tenantSlug;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;

    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const response = await fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    });

    let data: ApiResponse<T>;

    try {
      data = await response.json();
    } catch {
      throw new ApiError(
        'PARSE_ERROR',
        'Failed to parse server response'
      );
    }

    if (!response.ok || !data.success) {
      throw new ApiError(
        data.error?.code || 'UNKNOWN_ERROR',
        data.error?.message || 'An unexpected error occurred',
        data.error?.details
      );
    }

    return data.data as T;
  }

  /**
   * Initialize widget and fetch tenant configuration
   */
  async initWidget(): Promise<WidgetInitResponse> {
    return this.request<WidgetInitResponse>(
      `/api/v1/widget/init?tenant=${encodeURIComponent(this.tenantSlug)}`,
      { method: 'GET' }
    );
  }

  /**
   * Start a new conversation
   */
  async startConversation(sessionId: string, visitorId: string): Promise<ConversationResponse> {
    const raw = await this.request<Record<string, unknown>>(
      `/api/v1/widget/conversations`,
      {
        method: 'POST',
        body: JSON.stringify({
          tenant_slug: this.tenantSlug,
          session_id: sessionId,
          visitor_id: visitorId,
        }),
      }
    );
    const result = transformConversation(raw);
    // Backend doesn't return session_id in conversation response, so carry it through
    result.sessionId = sessionId;
    return result;
  }

  /**
   * Send a message in an existing conversation
   */
  async sendMessage(
    conversationId: string,
    sessionId: string,
    content: string,
    wasVoiceInput: boolean = false
  ): Promise<SendMessageResponse> {
    const raw = await this.request<Record<string, unknown>>(
      `/api/v1/widget/conversations/${conversationId}/messages?session_id=${encodeURIComponent(sessionId)}`,
      {
        method: 'POST',
        body: JSON.stringify({
          content,
          was_voice_input: wasVoiceInput,
        }),
      }
    );
    return transformSendMessageResponse(raw, conversationId);
  }

  /**
   * Get conversation history
   */
  async getConversation(
    conversationId: string,
    sessionId: string
  ): Promise<ConversationResponse> {
    const raw = await this.request<Record<string, unknown>>(
      `/api/v1/widget/conversations/${conversationId}?session_id=${encodeURIComponent(sessionId)}`,
      { method: 'GET' }
    );
    return transformConversation(raw);
  }

  /**
   * Accept an offer and get PaymentIntent client secret for inline card payment
   */
  async acceptOffer(
    conversationId: string,
    sessionId: string,
    paymentType: 'deposit' | 'full'
  ): Promise<PaymentIntentResponse> {
    const raw = await this.request<Record<string, unknown>>(
      `/api/v1/widget/conversations/${conversationId}/accept-offer?session_id=${encodeURIComponent(sessionId)}`,
      {
        method: 'POST',
        body: JSON.stringify({ payment_type: paymentType }),
      }
    );
    return {
      orderId: raw.order_id as string,
      orderNumber: raw.order_number as string,
      clientSecret: raw.client_secret as string,
      amountCents: raw.amount_cents as number,
      currency: raw.currency as string,
    };
  }

  /**
   * Check payment status for an order
   */
  async checkPaymentStatus(conversationId: string, sessionId: string): Promise<PaymentStatusResponse> {
    return this.request<PaymentStatusResponse>(
      `/api/v1/widget/conversations/${conversationId}/payment-status?session_id=${encodeURIComponent(sessionId)}`,
      { method: 'GET' }
    );
  }

  /**
   * Transcribe audio to text
   */
  async transcribeAudio(audioBlob: Blob): Promise<SpeechToTextResponse> {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');

    const url = `${this.baseUrl}/api/v1/widget/speech-to-text?tenant=${encodeURIComponent(this.tenantSlug)}`;

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    let data: ApiResponse<SpeechToTextResponse>;

    try {
      data = await response.json();
    } catch {
      throw new ApiError(
        'PARSE_ERROR',
        'Failed to parse transcription response'
      );
    }

    if (!response.ok || !data.success) {
      throw new ApiError(
        data.error?.code || 'TRANSCRIPTION_ERROR',
        data.error?.message || 'Failed to transcribe audio',
        data.error?.details
      );
    }

    return data.data as SpeechToTextResponse;
  }
}

// Factory function to create ApiService instance
export function createApiService(baseUrl: string, tenantSlug: string): ApiService {
  return new ApiService(baseUrl, tenantSlug);
}
